import i18n from "src/i18n/config";
import { z } from "zod";

const resourceTypes = ["bool", "string", "integer", "dimen", "color"] as const;

type ResourceType = (typeof resourceTypes)[number];

export const resourceTypeOptions = resourceTypes.map((value) => ({ value, label: value }));

type OverlayTarget = {
    targetPackage: string;
    resourcePath: string;
    resourceName: string;
    resourceType: ResourceType;
    targetName?: string | null;
};

// osbuilder identifies a resource by the values folder it lands in, not by the file inside it: every file
// in a res/values* folder merges into one resource table, so the same name and type in defaults.xml and in
// overrides.xml collides, while the same name under a different type is a separate resource and is fine
const resourceFolder = (resourcePath: string) => resourcePath.trim().split("/").slice(0, -1).join("/");

const isSameOverlayTarget = (a: OverlayTarget, b: OverlayTarget) =>
    a.targetPackage.trim() === b.targetPackage.trim() &&
    resourceFolder(a.resourcePath) === resourceFolder(b.resourcePath) &&
    a.resourceType === b.resourceType &&
    a.resourceName.trim() === b.resourceName.trim();

export const hasDuplicateOverlay = (overlays: OverlayTarget[] | null | undefined, candidate: OverlayTarget) =>
    (overlays ?? []).some((overlay) => isSameOverlayTarget(overlay, candidate));

// osbuilder puts every entry for one target package into a single overlay package and dies when those
// entries disagree on the target name. A missing name counts as its own value there, so a blank entry
// cannot sit next to a named one — either every entry for the package repeats the same name, or none of
// them carries one.
const normalizeTargetName = (targetName?: string | null) => targetName?.trim() || null;

export const getTargetNameConflict = (overlays: OverlayTarget[] | null | undefined, candidate: OverlayTarget) => {
    const candidateName = normalizeTargetName(candidate.targetName);

    const conflict = (overlays ?? []).find(
        (overlay) =>
            overlay.targetPackage.trim() === candidate.targetPackage.trim() &&
            normalizeTargetName(overlay.targetName) !== candidateName,
    );

    return conflict ? { targetName: normalizeTargetName(conflict.targetName) } : null;
};

const valueChecks: Partial<Record<ResourceType, { pattern: RegExp; message: string }>> = {
    bool: {
        pattern: /^(true|false)$/,
        message: i18n.t("modals:manageProductModules.addOverlay.validation.bool"),
    },
    integer: {
        pattern: /^-?\d+$/,
        message: i18n.t("modals:manageProductModules.addOverlay.validation.integer"),
    },
    color: {
        pattern: /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
        message: i18n.t("modals:manageProductModules.addOverlay.validation.color"),
    },
    dimen: {
        pattern: /^(0|-?\d+(\.\d+)?(dp|dip|sp|px|pt|in|mm))$/,
        message: i18n.t("modals:manageProductModules.addOverlay.validation.dimen"),
    },
};

// idmap2 drops an overlay whose resource name is not a valid Android identifier, and the build step that
// generates it fails outright, so reject the name here rather than at build time
const androidIdentifier = /^[A-Za-z_][A-Za-z0-9_]*$/;

// only res/values* files can be overridden this way, qualifiers such as res/values-de included
const valuesResourcePath = /^res\/values[^/]*\/[^/]+\.xml$/;

const requiredString = z
    .string({ message: i18n.t("common:validation.fieldRequired") })
    .trim()
    .min(1, { message: i18n.t("common:validation.fieldRequired"), abort: true });

export const overlayValidationSchema = z
    .object({
        targetPackage: requiredString,
        resourcePath: requiredString.regex(valuesResourcePath, {
            message: i18n.t("modals:manageProductModules.addOverlay.validation.resourcePath"),
        }),
        resourceName: requiredString.regex(androidIdentifier, {
            message: i18n.t("modals:manageProductModules.addOverlay.validation.resourceName"),
        }),
        resourceType: z.enum(resourceTypes, { message: i18n.t("common:validation.fieldRequired") }),
        value: requiredString,
        targetName: z.string().nullish(),
    })
    .superRefine((values, ctx) => {
        const value = values.value.trim();
        // Android and osbuilder accept a resource reference (@bool/foo, ?attr/colorPrimary) as any typed value
        if (/^[@?]/.test(value)) {
            return;
        }

        const check = valueChecks[values.resourceType];
        if (check && !check.pattern.test(value)) {
            ctx.addIssue({ code: "custom", path: ["value"], message: check.message });
        }
    });
